import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlarmClock, CalendarX, ChevronRight, FlaskConical, Lock, RotateCcw, Route, Stethoscope, UserX, type LucideIcon,
} from 'lucide-react';
import {
  Alert, DataTable, Pagination, EmptyState, SearchInput, StatusBadge, type Tone, cn,
} from '@dru/ui';
import { EXAM_STATUS_LABEL, EXAM_TYPE_LABEL, type ExamStatus } from '@dru/types';
import { useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { formatDate, formatDateTime } from '../../../lib/format';
import { nextStep } from '../components/caseNextStep';

const PAGE_SIZE = 10;

/**
 * One row of Operations' case queue, as the server projects it
 * (platform/services/case-view.service.ts). Coordination only: there is no
 * result, finding or decision in this shape to render, which is the point —
 * the page cannot show what the API never sends.
 */
export interface OpsCase {
  id: string;
  company: { id: string; name: string };
  plant: { id: string; code: string; name: string } | null;
  employee: { employeeCode: string; fullName: string };
  examinationType: string;
  package: { code: string; name: string } | null;
  status: string;
  priority: string;
  stage: { status: string; since: string } | null;
  dueDate: string | null;
  slaDueAt: string | null;
  slaBreachedAt: string | null;
  assignedPartner: { id: string; name: string | null } | null;
  assignedLocation: { id: string; name: string; city: string | null } | null;
  requestedLocation: { id: string; name: string; city: string | null; partnerName: string | null } | null;
  requestedLocationNote: string | null;
  physician: { id: string; fullName: string } | null;
  /** Why a case at physician review is not moving; null when it is. */
  physicianWait?: 'NO_PHYSICIAN' | 'CANNOT_REVIEW' | 'AWAY' | 'OVERDUE' | null;
  appointment: { id: string; scheduledFor: string; status: string } | null;
  exceptions: string[];
  updatedAt?: string;
}

/** Why a case is waiting on a physician, in the words of what to do about it. */
export const PHYSICIAN_WAIT_LABEL: Record<string, string> = {
  NO_PHYSICIAN: 'No physician assigned',
  CANNOT_REVIEW: 'Assigned physician cannot take it',
  AWAY: 'Assigned physician is away',
  OVERDUE: 'Waiting more than 48 hours',
};

/** A case Operations has nothing left to coordinate on (case-view.service CLOSED_STATUSES). */
export const CLOSED = new Set(['CERTIFIED', 'CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED']);

/**
 * What is stuck on a case, in the words of the act that unsticks it.
 *
 * "No lab assigned" was wrong for half the cases it marked: the server flags
 * every case still REQUESTED, and some of those already carry a proposed lab.
 * What they share is that Operations has not routed them yet.
 */
export const EXCEPTION_LABEL: Record<string, string> = {
  SLA_BREACHED: 'SLA breached',
  UNASSIGNED: 'Needs routing',
  NO_APPOINTMENT: 'Not booked',
  NO_SHOW: 'No-show',
  RETEST_REQUIRED: 'Retest required',
  SLOT_PASSED: 'Slot passed — not checked in',
  AWAITING_PHYSICIAN: 'Waiting on a physician',
};

export const EXCEPTION_TONE: Record<string, Tone> = {
  SLA_BREACHED: 'overdue', UNASSIGNED: 'dueSoon', NO_APPOINTMENT: 'dueSoon',
  NO_SHOW: 'overdue', RETEST_REQUIRED: 'pending', SLOT_PASSED: 'dueSoon', AWAITING_PHYSICIAN: 'overdue',
};

/** What each "stuck" card says and shows. */
const EXCEPTION_CARD: Record<string, { icon: LucideIcon; hint: string }> = {
  UNASSIGNED: { icon: Route, hint: 'Send to a lab' },
  NO_APPOINTMENT: { icon: CalendarX, hint: 'Book a slot' },
  AWAITING_PHYSICIAN: { icon: Stethoscope, hint: 'Doctor to review' },
  SLA_BREACHED: { icon: AlarmClock, hint: 'Past the promised date' },
  NO_SHOW: { icon: UserX, hint: 'Rebook the employee' },
  RETEST_REQUIRED: { icon: RotateCcw, hint: 'Test to repeat' },
};

/** "in 12 days", "today", "3 days late". */
function dueIn(iso: string): { text: string; late: boolean; soon: boolean } {
  const day = 86_400_000;
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const n = Math.round((d.getTime() - start.getTime()) / day);
  if (n === 0) return { text: 'Due today', late: false, soon: true };
  if (n < 0) return { text: `${-n} day${n === -1 ? '' : 's'} late`, late: true, soon: false };
  return { text: `In ${n} day${n === 1 ? '' : 's'}`, late: false, soon: n <= 7 };
}

/** Stable order for the cards: the act Operations owns first. */
const EXCEPTION_ORDER = ['UNASSIGNED', 'NO_APPOINTMENT', 'AWAITING_PHYSICIAN', 'SLA_BREACHED', 'NO_SHOW', 'RETEST_REQUIRED'] as const;

/** The two views beyond the open queue: a certified or cancelled case has to be findable too. */
const VIEWS: Record<string, { label: string; view: 'closed' | 'all' }> = {
  closed: { label: 'Closed', view: 'closed' },
  all: { label: 'All cases', view: 'all' },
};

export const humanStatus = (s: string) => s.replace(/_/g, ' ').toLowerCase();
/** A case status or examination type in words, never the enum. */
export const statusLabel = (s: string) => EXAM_STATUS_LABEL[s as ExamStatus] ?? humanStatus(s);
export const typeLabel = (c: Pick<OpsCase, 'package' | 'examinationType'>) =>
  c.package?.name ?? EXAM_TYPE_LABEL[c.examinationType] ?? humanStatus(c.examinationType);

/** The most the case endpoint returns in one call; it does not page. */
const CASE_LIMIT = 200;

/**
 * Operations' case queue.
 *
 * Opens on the question Operations is asked all day — which cases need
 * somebody to act — with the count for each kind of "stuck" on its tab, so
 * the queue for routing is one click away and its size is visible before it
 * is opened. Each row reads left to right: who, where the case is, which lab
 * and when, and by when it is due. Workflow only: the API sends nothing
 * clinical, and nothing here asks for it.
 *
 * Reachable by URL for a role without `case.read-ops` (Support has its own
 * desk); such a reader is told where to go instead of shown a 403.
 */
export function Cases() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const allowed = holds(permissions, 'case.read-ops');
  const [exception, setException] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  // The search goes to the server too, a moment after typing stops: the
  // closed cases are not on the page until they are asked for.
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);
  const view = VIEWS[exception]?.view;

  // The whole open queue, for the counts on the tabs and the notice.
  const all = useQuery<{ items: OpsCase[] }>(
    () => (allowed ? api.get(`/platform/cases?limit=${CASE_LIMIT}`) : Promise.resolve({ items: [] })),
    [allowed],
  );
  // The filtered view is asked of the server, which filters before its
  // limit, so a tab is complete even when the whole queue is not. So is a
  // search, so it is not limited to the cases the page already holds.
  const filtered = useQuery<{ items: OpsCase[] }>(
    () => (allowed && (exception || q)
      ? api.get(`/platform/cases?limit=${CASE_LIMIT}`
        + (view ? `&view=${view}` : exception ? `&exception=${exception}` : '')
        + (q ? `&q=${encodeURIComponent(q)}` : ''))
      : Promise.resolve({ items: [] })),
    [allowed, exception, view, q],
  );
  const source = exception || q ? filtered : all;

  const counts = useMemo(() => {
    const n: Record<string, number> = {};
    for (const c of all.data?.items ?? []) for (const e of c.exceptions) n[e] = (n[e] ?? 0) + 1;
    return n;
  }, [all.data]);

  const needle = term.trim().toLowerCase();
  const rows = (source.data?.items ?? []).filter((c) => !needle || [
    c.employee.fullName, c.employee.employeeCode, c.company.name, c.plant?.name, c.assignedPartner?.name,
  ].some((v) => v?.toLowerCase().includes(needle)));

  // Ten at a time (owner, 29 Oct 2026). A new tab or search starts at page 1;
  // done while rendering so the old page is never shown with the new list.
  const asked = `${exception}|${needle}`;
  const [lastAsked, setLastAsked] = useState(asked);
  if (asked !== lastAsked) { setLastAsked(asked); setPage(1); }
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const pageRows = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  if (!allowed) {
    return (
      <div className="space-y-4">
        <PageHeader className="mb-0" title="Medical cases" />
        <EmptyState
          icon={<Lock className="h-5 w-5" />}
          title="The case queue is for Operations"
          hint={holds(permissions, 'case.read-support')
            ? 'Search the employee’s name or mobile in the box at the top: it shows where and when, without anything clinical.'
            : 'Your role does not route medical cases.'}
        />
      </div>
    );
  }


  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Medical cases"
        hint="Cases across every company: where each one is, which lab has it, and what is stuck. Operations sees the workflow only — no results or clinical notes."
      />

      {/* What is stuck, as cards: the count is the first thing read, and a
          card filters the list to it. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6" role="group" aria-label="What needs attention">
        {EXCEPTION_ORDER.map((k) => {
          const meta = EXCEPTION_CARD[k]!;
          const n = counts[k] ?? 0;
          const on = exception === k;
          const red = EXCEPTION_TONE[k] === 'overdue';
          return (
            <button
              key={k} type="button" aria-pressed={on}
              onClick={() => setException(on ? '' : k)}
              className={cn(
                'flex min-h-[5.5rem] flex-col justify-between rounded-card border bg-white p-3 text-left transition-colors',
                on ? 'border-primary-400 ring-2 ring-primary-100' : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50',
                n === 0 && !on && 'opacity-60',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-caption font-medium text-neutral-600">{EXCEPTION_LABEL[k]}</span>
                <meta.icon aria-hidden className={cn('h-4 w-4 shrink-0', n === 0 ? 'text-neutral-400' : red ? 'text-danger-600' : 'text-warning-600')} />
              </span>
              <span className="flex items-baseline justify-between gap-2">
                <span className={cn('text-h3 font-semibold tabular-nums', n === 0 ? 'text-neutral-400' : red ? 'text-danger-600' : 'text-neutral-900')}>
                  {all.data ? n : '–'}
                </span>
                <span className="truncate text-micro text-neutral-500">{meta.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div role="group" aria-label="Which cases" className="flex h-10 shrink-0 rounded-btn border border-neutral-200 bg-white p-0.5">
          {([
            ['', 'Open', all.data?.items.length],
            ['closed', 'Closed', undefined],
            ['all', 'All', undefined],
          ] as const).map(([k, label, n]) => {
            const on = k === '' ? !view : exception === k;
            return (
              <button
                key={k} type="button" aria-pressed={on} onClick={() => setException(k)}
                className={cn(
                  'inline-flex flex-1 items-center justify-center gap-1.5 rounded-[6px] px-3.5 text-small font-medium transition-colors sm:flex-none',
                  on ? 'bg-primary-50 text-primary-800' : 'text-neutral-600 hover:text-neutral-900',
                )}
              >
                {label}
                {n !== undefined && <span className="tabular-nums text-neutral-500">{n}</span>}
              </button>
            );
          })}
        </div>
        <SearchInput
          value={term} onValueChange={setTerm}
          placeholder="Employee, employee code, company or lab" aria-label="Search cases"
          className="w-full sm:max-w-md"
        />
        {exception && !view && (
          <button type="button" onClick={() => setException('')} className="text-small font-medium text-primary-600 hover:text-primary-700 sm:ml-auto">
            Showing “{EXCEPTION_LABEL[exception]}” · Show all open
          </button>
        )}
      </div>

      {/* The case list does not page yet (it is merged across companies on
          the server), so say so rather than imply the list is complete. */}
      {(source.data?.items.length ?? 0) >= CASE_LIMIT && (
        <Alert tone="info">
          Showing the first {CASE_LIMIT} cases, {view ? 'most recently changed first' : 'soonest SLA first'}. Choose a tab above or search to narrow the list.
        </Alert>
      )}

      <DataTable<OpsCase>
        rowNumberStart={(current - 1) * PAGE_SIZE + 1}
        rows={pageRows}
        loading={source.loading}
        error={source.error}
        onRetry={source.reload}
        rowKey={(c) => c.id}
        onRowClick={(c) => navigate(`/ops/cases/${c.company.id}/${c.id}`)}
        empty={needle
          ? {
              title: 'No case matches the search',
              hint: view ? 'Search looks at the employee’s name and code, and the company.'
                : 'Search looks at the employee’s name and code, and the company. Closed cases are under Closed.',
            }
          : view === 'closed'
            ? { title: 'No closed cases', hint: 'A case closes when it is certified, cancelled or expires.' }
          : exception && !view
            ? { title: `No case under “${EXCEPTION_LABEL[exception] ?? exception}”`, hint: 'No open case has this problem right now.' }
            : { title: 'No open cases', hint: 'Cases appear here when a company raises a medical examination.' }}
        columns={[
          {
            key: 'worker', header: 'Employee', alwaysVisible: true, minWidth: 200,
            cell: (c) => (
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900">{c.employee.fullName}</div>
                <div className="truncate text-caption font-normal text-neutral-500" title={[c.employee.employeeCode, c.company.name, c.plant?.name].filter(Boolean).join(' · ')}>
                  <span className="font-mono">{c.employee.employeeCode}</span> · {c.company.name}
                </div>
              </div>
            ),
          },
          {
            // Where the case is, and — when it is stuck — why, together:
            // the stage is the noun, the problem the adjective.
            key: 'status', header: 'Status', mobileLabel: false, minWidth: 180,
            cell: (c) => (
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* One value (owner, 30 Oct 2026): the problem when there is
                      one, otherwise the stage — never both, they say the same. */}
                  {c.exceptions.length === 0 && <span className="font-medium text-neutral-900">{statusLabel(c.status)}</span>}
                  {c.exceptions.map((e) => (
                    <StatusBadge key={e} tone={EXCEPTION_TONE[e] ?? 'neutral'}>{EXCEPTION_LABEL[e] ?? e}</StatusBadge>
                  ))}
                </div>
                {/* Why a review is stuck is the Next step's ("Chase or
                    reassign physician"); here it stays a tooltip. */}
                <div className="truncate text-caption font-normal text-neutral-500"
                     title={c.physicianWait ? PHYSICIAN_WAIT_LABEL[c.physicianWait] : undefined}>
                  {typeLabel(c)}
                </div>
              </div>
            ),
          },
          {
            key: 'lab', header: 'Lab and appointment', mobileLabel: 'Lab', stackFull: true, minWidth: 190,
            cell: (c) => {
              const routed = c.status !== 'REQUESTED' && c.assignedPartner;
              return (
                <div className="flex min-w-0 max-w-[15rem] items-start gap-2 max-md:max-w-none">
                  <FlaskConical aria-hidden className={cn('mt-0.5 hidden h-4 w-4 shrink-0 lg:block', routed ? 'text-neutral-400' : 'text-neutral-300')} />
                  <div className="min-w-0">
                    {routed
                      ? <div className="truncate text-neutral-900" title={c.assignedPartner!.name ?? ''}>{c.assignedPartner!.name}</div>
                      : <div className="text-neutral-500">Not routed yet</div>}
                    <div className="truncate text-caption font-normal text-neutral-500">
                      {!routed && c.assignedPartner?.name
                        ? `Proposed: ${c.assignedPartner.name}`
                        : c.appointment ? formatDateTime(c.appointment.scheduledFor) : 'No appointment'}
                    </div>
                  </div>
                </div>
              );
            },
          },
          {
            key: 'sla', header: 'Due', minWidth: 100,
            cell: (c) => {
              const due = c.slaDueAt ?? c.dueDate;
              if (!due) return <span className="text-neutral-500">No date</span>;
              const r = dueIn(due);
              const late = r.late || !!c.slaBreachedAt;
              return (
                <div>
                  <div className={cn('whitespace-nowrap font-medium tabular-nums', late ? 'text-danger-600' : r.soon ? 'text-warning-600' : 'text-neutral-900')}>{r.text}</div>
                  <div className="whitespace-nowrap text-caption font-normal text-neutral-500">{formatDate(due)}{c.slaDueAt ? ' · SLA' : ''}</div>
                </div>
              );
            },
          },
          {
            // Whose move it is, and what. Operations' own acts read as a
            // link-coloured instruction; everyone else's as who it waits on.
            key: 'next', header: 'Next step', alwaysVisible: true, minWidth: 200,
            cell: (c) => {
              const n = nextStep(c);
              const ours = n.team === 'Operations';
              return (
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className={cn('whitespace-nowrap text-small', ours ? 'font-medium text-primary-700' : 'text-neutral-800')}>{n.action}</div>
                    <div className={cn('text-caption font-normal', ours ? 'text-primary-700' : 'text-neutral-500')}>
                      {ours ? 'Operations' : n.team === 'None' ? 'Done' : `Waiting on ${n.team.toLowerCase() === 'hr' ? 'HR' : n.team.toLowerCase()}`}
                    </div>
                  </div>
                  <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-neutral-400 max-md:hidden" />
                </div>
              );
            },
          },
        ]}
      />
      {rows.length > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination
            page={current} pageSize={PAGE_SIZE} total={rows.length}
            onPageChange={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        </div>
      )}
    </div>
  );
}
